/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/emailFlowErrors.scss';

import React, {ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {EmailStepError} from '../../util/types';

import {WithBackground} from '@dvrd/dvr-controls';

import {BluemateButton} from '../controls';

import {translateEmailStepError} from '../../util/bolValidation';

import {ActivateHandle} from '../../util/interfaces';

import WarnIcon from '../icon/warnIcon';

import ErrorIcon from '../icon/errorIcon';

function EmailFlowErrors(_: object, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [errors, setErrors] = useState<Array<EmailStepError>>([]);
    const [action, setAction] = useState('');

    function onActivate(errors: Array<EmailStepError>, action: string) {
        setErrors(errors);
        setAction(action);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setErrors([]);
        setAction('');
    }

    const groupedErrors: Record<string, Array<EmailStepError>> = useMemo(() => {
        const grouped: Record<string, Array<EmailStepError>> = {};
        for (const error of errors) {
            let stepLabel: string;
            if (error.reason === 'overlap') stepLabel = error.step1_label;
            else if (error.reason === 'content') stepLabel = error.step_label;
            else continue;
            if (!Object.hasOwn(grouped, stepLabel)) grouped[stepLabel] = [];
            grouped[stepLabel].push(error);
        }
        return grouped;
    }, [errors]);

    function renderBody() {
        return (
            <div className='body-container'>
                <p className='error-message'>
                    {action === 'inschakelen'
                        ? t(
                              'email.flowErrors.enableFailedBody',
                              'Het inschakelen is niet gelukt doordat de volgende problemen gevonden zijn:'
                          )
                        : t(
                              'email.flowErrors.disableFailedBody',
                              'Het uitschakelen is niet gelukt doordat de volgende problemen gevonden zijn:'
                          )}
                </p>
                {Object.entries(groupedErrors).map(renderErrors)}
            </div>
        );
    }

    function renderErrors([label, errors]: [string, Array<EmailStepError>], index: number) {
        return (
            <div key={index} className='step-error'>
                <label className='step-label'>{label}</label>
                {errors.map(renderError)}
            </div>
        );
    }

    function renderError(error: EmailStepError, index: number) {
        let icon: ReactNode = null;
        if (error.reason === 'content') icon = <WarnIcon />;
        else if (error.reason === 'overlap') icon = <ErrorIcon />;
        return (
            <label key={index} className='error-label'>
                {icon} {translateEmailStepError(error, true)}
            </label>
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <WithBackground onClose={onClose} active={active}>
            <div className='default-popup email-step-errors'>
                <div className='header-container'>
                    <label className='header-title'>
                        {action === 'inschakelen'
                            ? t('email.flow.enableFailedTitle', 'Inschakelen mislukt')
                            : t('email.flow.disableFailedTitle', 'Uitschakelen mislukt')}
                    </label>
                </div>
                {renderBody()}
                <div className='footer-container btn'>
                    <BluemateButton onClick={onClose} label={t('common.dialog.ok', 'Oké')} />
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, object>(EmailFlowErrors);
